import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, Divider, IconButton, Paper, Stack,
  Typography, useTheme,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { fetchResultadosSevac } from "../utils/contentfulRequests";

const CuentaPublica = () => {
  const theme = useTheme();
  const [resultados, setResultados] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResultadosSevac("Cuenta publica Entry")
      .then(setResultados)
      .finally(() => setLoading(false));
  }, []);

  const groupedResults = useMemo(() => resultados.reduce((groups, resultado) => {
    const year = resultado.ano || "Sin año";
    groups[year] = groups[year] || [];
    groups[year].push(resultado);
    return groups;
  }, {}), [resultados]);
  const years = Object.keys(groupedResults).filter((year) => year !== "Sin año").sort((a, b) => Number(b) - Number(a));
  const activeYear = selectedYear || years[0];
  const activeResults = activeYear ? groupedResults[activeYear] || [] : [];

  return (
    <Box sx={{ minHeight: "70vh", backgroundColor: "#f7f9fc", py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1120, mx: "auto" }}>
        <Box sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ display: "grid", placeItems: "center", width: 48, height: 48, borderRadius: 2, color: "#fff", backgroundColor: theme.palette.primary.main }}>
              <AccountBalanceOutlinedIcon />
            </Box>
            <Typography sx={{ color: theme.palette.highlight.main, fontSize: 13, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase" }}>
              Transparencia y rendición de cuentas
            </Typography>
          </Stack>
          <Typography component="h1" sx={{ color: theme.palette.primary.main, fontSize: { xs: 34, md: 48 }, lineHeight: 1.08, fontWeight: 800, mb: 1.5 }}>
            Cuenta Pública
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 17, maxWidth: 680 }}>
            Consulta los informes y documentos de la Cuenta Pública organizados por año.
          </Typography>
        </Box>

        {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box> : years.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}><Typography color="text.secondary">No hay resultados disponibles.</Typography></Paper>
        ) : (
          <>
            <Paper elevation={0} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, mb: 3, overflowX: "auto", border: "1px solid #e1e8f0", borderRadius: 2.5, backgroundColor: "#fff" }}>
              <CalendarMonthOutlinedIcon sx={{ ml: 1, color: theme.palette.primary.main }} />
              {years.map((year) => <Button key={year} onClick={() => setSelectedYear(year)} sx={{ minWidth: 92, borderRadius: 1.5, color: activeYear === year ? "#fff" : theme.palette.primary.main, backgroundColor: activeYear === year ? theme.palette.primary.main : "transparent", fontWeight: 700, "&:hover": { backgroundColor: activeYear === year ? theme.palette.primary.main : "#edf4fb" } }}>{year}</Button>)}
            </Paper>
            <Stack spacing={2}>
              {activeResults.sort((a, b) => (a.trimestre || 0) - (b.trimestre || 0)).map((resultado) => (
                <Paper key={resultado.id || `${resultado.ano}-${resultado.trimestre}`} elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, border: "1px solid #e1e8f0", borderRadius: 2.5, backgroundColor: "#fff" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                    <Box><Typography sx={{ color: theme.palette.primary.main, fontSize: 20, fontWeight: 800 }}>{resultado.titulo || `Informe ${resultado.trimestre ? `del ${resultado.trimestre}° trimestre` : ""}`}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Cuenta Pública {resultado.ano}</Typography></Box>
                    <Typography sx={{ color: theme.palette.highlight.main, fontWeight: 800, whiteSpace: "nowrap" }}>{resultado.documentos?.length || 0} documento{resultado.documentos?.length === 1 ? "" : "s"}</Typography>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    {(resultado.documentos || []).map((documento, index) => (
                      <Box key={`${documento.nombre}-${index}`} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, p: 1, borderRadius: 1.5, backgroundColor: "#f7f9fc" }}>
                        <Typography sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#243b53", fontWeight: 600 }} title={documento.nombre}>{documento.nombre}</Typography>
                        {documento.pdf && <><IconButton component="a" href={documento.pdf} target="_blank" rel="noopener noreferrer" aria-label={`Ver ${documento.nombre}`} size="small" sx={{ color: theme.palette.primary.main }}><VisibilityOutlinedIcon fontSize="small" /></IconButton><IconButton component="a" href={documento.pdf} target="_blank" rel="noopener noreferrer" download aria-label={`Descargar ${documento.nombre}`} size="small" sx={{ color: theme.palette.highlight.main }}><DownloadOutlinedIcon fontSize="small" /></IconButton></>}
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CuentaPublica;